import React, { useEffect, useMemo, useState } from "react";
import * as t from "io-ts";
import * as FirstPartyFetchResponse from "../../../../../domain/models/FirstPartyFetchResponse";
import { getJson } from "../../../wrappers/restRequest";
import { Background } from "../../../components/BuildingBlocks/Background";
import { Padding } from "../../../components/BuildingBlocks/Padding";
import { SpacingColumn } from "../../../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../../components/BuildingBlocks/SpacingRow";
import { Flex } from "../../../components/BuildingBlocks/Flex";
import { FontRobotoSlab } from "../../../components/BuildingBlocks/FontRobotoSlab";
import { FontQuicksand } from "../../../components/BuildingBlocks/FontQuicksand";
import { FontSize } from "../../../components/BuildingBlocks/FontSize";
import { TextColor } from "../../../components/BuildingBlocks/TextColor";
import { Uppercase } from "../../../components/Uppercase/Uppercase";
import { WeightMedium } from "../../../components/WeightMedium/WeightMedium";
import { WeightBold } from "../../../components/WeightBold/WeightBold";
import { TextCenter } from "../../../components/TextCenter/TextCenter";
import { FrontColors } from "../../../models/FrontColors";
import { FrontFontSizes } from "../../../models/FrontFontSizes";
import { FrontSpacing } from "../../../models/FrontSpacing";
import { FPSimpleLogo } from "../../../components/FrontProbate/Simple/FPSimpleLogo/FPSimpleLogo";
import { FPLandingTitle } from "../../../components/FrontProbate/Simple/FPLandingTitle/FPLandingTitle";
import { MaxWidth } from "../../../components/BuildingBlocks/MaxWidth";
import { TextAlign } from "../../../components/BuildingBlocks/TextAlign";
import { WeightSemiBold } from "../../../components/WeightSemiBold/WeightSemiBold";
import { CursorPointer } from "../../../components/CursorPointer/CursorPointer";
import { TSort, TSortKey, nextSortAfterClick, sortCases } from "./sortCases";
import { introducerPortalBasePath } from "../getIntroducerSlugFromUrl";
import { getAsIntroducerIdFromUrl, withAsIntroducerId } from "../asIntroducerId";

const SelfDataCodec = t.union([
    t.null,
    t.type({
        user: t.type({
            id: t.string,
            first_name: t.string,
            last_name: t.string,
            email: t.union([t.string, t.null]),
        }),
        introducer: t.type({
            id: t.string,
            name: t.string,
            slug: t.union([t.string, t.null]),
            brand_logo_url: t.union([t.string, t.null]),
            brand_primary_colour: t.union([t.string, t.null]),
        }),
        is_introducer_admin: t.boolean,
        is_impersonating: t.boolean,
    }),
]);
type TSelfData = t.TypeOf<typeof SelfDataCodec>;

const SalesBucketsCodec = t.type({
    valuation_in_progress: t.number,
    awaiting_instruction: t.number,
    on_the_market: t.number,
    at_auction: t.number,
    under_offer: t.number,
    completed: t.number,
    lost: t.number,
});

const ConveyancingBucketsCodec = t.type({
    awaiting_instruction: t.number,
    instructed: t.number,
    ready_to_exchange: t.number,
    exchanged: t.number,
    completed: t.number,
    closed: t.number,
});

const DashboardDataCodec = t.union([
    t.null,
    t.type({
        sales_buckets: SalesBucketsCodec,
        conveyancing_buckets: ConveyancingBucketsCodec,
        needs_attention_count: t.number,
        total_cases: t.number,
    }),
]);
type TDashboardData = t.TypeOf<typeof DashboardDataCodec>;

const CaseAddressCodec = t.type({
    building_name: t.union([t.string, t.null]),
    building_number: t.union([t.string, t.null]),
    sub_building_name: t.union([t.string, t.null]),
    sub_building_number: t.union([t.string, t.null]),
    street_name: t.union([t.string, t.null]),
    city_town: t.union([t.string, t.null]),
    locality: t.union([t.string, t.null]),
    county: t.union([t.string, t.null]),
    country: t.union([t.string, t.null]),
    postcode: t.union([t.string, t.null]),
});
type TCaseAddress = t.TypeOf<typeof CaseAddressCodec>;

const SectionCodec = t.union([t.literal("sales"), t.literal("conveyancing")]);
type TSection = t.TypeOf<typeof SectionCodec>;

const LegalStatusCodec = t.union([
    t.literal("instructed"),
    t.literal("ready_to_exchange"),
    t.literal("exchanged"),
    t.literal("completed"),
]);
type TLegalStatus = t.TypeOf<typeof LegalStatusCodec>;

const BucketCodec = t.union([
    t.literal("valuation_in_progress"),
    t.literal("awaiting_instruction"),
    t.literal("on_the_market"),
    t.literal("at_auction"),
    t.literal("under_offer"),
    t.literal("instructed"),
    t.literal("ready_to_exchange"),
    t.literal("exchanged"),
    t.literal("completed"),
    t.literal("lost"),
    t.literal("closed"),
]);
type TBucket = t.TypeOf<typeof BucketCodec>;

const CaseCodec = t.type({
    id: t.string,
    section: SectionCodec,
    bucket: BucketCodec,
    address: t.union([CaseAddressCodec, t.null]),
    days_on_market: t.union([t.number, t.null]),
    offers_count_last_30d: t.number,
    viewings_count_last_30d: t.number,
    legal_status: t.union([LegalStatusCodec, t.null]),
    outstanding_introducer_docs: t.number,
});
type TCase = t.TypeOf<typeof CaseCodec>;

const CasesDataCodec = t.array(CaseCodec);

type TSalesBucket = keyof t.TypeOf<typeof SalesBucketsCodec>;
type TConveyancingBucket = keyof t.TypeOf<typeof ConveyancingBucketsCodec>;

const SALES_BUCKET_ORDER: ReadonlyArray<TSalesBucket> = [
    "valuation_in_progress",
    "awaiting_instruction",
    "on_the_market",
    "at_auction",
    "under_offer",
    "completed",
    "lost",
];

const SALES_BUCKET_LABELS: Record<TSalesBucket, string> = {
    valuation_in_progress: "Valuation in progress",
    awaiting_instruction: "Awaiting instruction",
    on_the_market: "On the market",
    at_auction: "At auction",
    under_offer: "Under offer",
    completed: "Completed",
    lost: "Lost",
};

const CONVEYANCING_BUCKET_ORDER: ReadonlyArray<TConveyancingBucket> = [
    "awaiting_instruction",
    "instructed",
    "ready_to_exchange",
    "exchanged",
    "completed",
    "closed",
];

const CONVEYANCING_BUCKET_LABELS: Record<TConveyancingBucket, string> = {
    awaiting_instruction: "Awaiting instruction",
    instructed: "Instructed",
    ready_to_exchange: "Ready to exchange",
    exchanged: "Exchanged",
    completed: "Completed",
    closed: "Closed",
};

const BUCKET_LABELS: Record<TBucket, string> = {
    valuation_in_progress: "Valuation in progress",
    awaiting_instruction: "Awaiting instruction",
    on_the_market: "On the market",
    at_auction: "At auction",
    under_offer: "Under offer",
    instructed: "Instructed",
    ready_to_exchange: "Ready to exchange",
    exchanged: "Exchanged",
    completed: "Completed",
    lost: "Lost",
    closed: "Closed",
};

const LEGAL_STATUS_LABELS: Record<TLegalStatus, string> = {
    instructed: "Legals: instructed",
    ready_to_exchange: "Legals: ready to exchange",
    exchanged: "Legals: exchanged",
    completed: "Legals: completed",
};

const formatAddress = (address: TCaseAddress | null): string => {
    if (!address) return "Unknown address";
    const buildingPart = [address.building_number, address.building_name].filter((p): p is string => !!p).join(" ");
    const parts = [buildingPart, address.street_name, address.city_town, address.postcode].filter((p): p is string => !!p && p.trim().length > 0);
    return parts.length === 0 ? "Unknown address" : parts.join(", ");
};

const redirectToLogin = (): void => {
    window.location.href = `${introducerPortalBasePath()}/login`;
};

const goToCase = (caseId: string, asIntroducerId: string | undefined): void => {
    window.location.href = withAsIntroducerId(`${introducerPortalBasePath()}/cases/${caseId}`, asIntroducerId);
};

const PurpleShell = (props: React.PropsWithChildren<{}>): JSX.Element => (
    <Background color={FrontColors.PROBATE_0} minHeight="100vh">
        <Padding type="custom" spacing={`${FrontSpacing.LARGE_3} ${FrontSpacing.MEDIUM_1}`}>
            <div className="ip-dashboard__shell-inner">
                {props.children}
            </div>
        </Padding>
    </Background>
);

const ImpersonationBanner = (props: { introducerName: string }): JSX.Element => (
    <Background color={FrontColors.PERTINENT_CORAL_4} borderRadius="5px">
        <Padding type="custom" spacing={`${FrontSpacing.SMALL_3} ${FrontSpacing.MEDIUM_1}`}>
            <FontQuicksand>
                <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                    <TextColor color={FrontColors.NEUTRAL_18}>
                        <WeightBold>Sail staff preview</WeightBold> — viewing {props.introducerName}&apos;s portal. Activity is logged to the introducer audit log.
                    </TextColor>
                </FontSize>
            </FontQuicksand>
        </Padding>
    </Background>
);

const StateHero = (props: { title: string; body: React.ReactNode }): JSX.Element => (
    <Flex justifyContent="center" alignItems="center" minHeight="60vh">
        <SpacingColumn spacing={FrontSpacing.LARGE_1} justifyContent="center">
            <Flex justifyContent="center">
                <FPSimpleLogo width="60px" />
            </Flex>
            <Flex justifyContent="center">
                <MaxWidth width="560px">
                    <FPLandingTitle size={48} color={FrontColors.NEUTRAL_18} align="center">
                        {props.title}
                    </FPLandingTitle>
                </MaxWidth>
            </Flex>
            <Flex justifyContent="center">
                <MaxWidth width="480px">
                    <FontQuicksand>
                        <FontSize size={FrontFontSizes.MED} lineHeight={1.45}>
                            <WeightSemiBold>
                                <TextColor color={FrontColors.NEUTRAL_18}>
                                    <TextAlign position="center">
                                        {props.body}
                                    </TextAlign>
                                </TextColor>
                            </WeightSemiBold>
                        </FontSize>
                    </FontQuicksand>
                </MaxWidth>
            </Flex>
        </SpacingColumn>
    </Flex>
);

const SectionLabel = (props: { children: React.ReactNode }): JSX.Element => (
    <FontRobotoSlab>
        <FontSize size={FrontFontSizes.X_SMALL}>
            <TextColor color={FrontColors.NEUTRAL_18}>
                <Uppercase>
                    <WeightMedium>{props.children}</WeightMedium>
                </Uppercase>
            </TextColor>
        </FontSize>
    </FontRobotoSlab>
);

const SortableHeader = (props: {
    label: string;
    sortKey: TSortKey;
    sort: TSort;
    onClick: (key: TSortKey) => void;
}): JSX.Element => {
    const isActive = props.sort.key === props.sortKey;
    const indicator = isActive ? (props.sort.direction === "asc" ? " ↑" : " ↓") : "";
    const className = `ip-dashboard__th ip-dashboard__th--sortable${isActive ? " ip-dashboard__th--active" : ""}`;
    return (
        <th className={className} onClick={() => props.onClick(props.sortKey)}>
            {props.label}{indicator}
        </th>
    );
};

const BucketTile = (props: {
    label: string;
    count: number;
    isSelected: boolean;
    onClick: () => void;
}): JSX.Element => (
    <CursorPointer onClick={props.onClick}>
        <Background
            color={props.isSelected ? FrontColors.PROBATE_4 : FrontColors.NEUTRAL_18}
            borderRadius="5px"
            borderDefinition={props.isSelected
                ? `solid 2px ${FrontColors.NEUTRAL_18}`
                : `solid 2px transparent`}
        >
            <Padding type="custom" spacing={`${FrontSpacing.SMALL_3} ${FrontSpacing.SMALL_2}`}>
                <SpacingColumn spacing={FrontSpacing.SMALL_1}>
                    <TextCenter>
                        <FontRobotoSlab>
                            <FontSize size={FrontFontSizes.X_LARGE} lineHeight="1.05">
                                <TextColor color={props.isSelected ? FrontColors.NEUTRAL_18 : FrontColors.PROBATE_0}>
                                    <WeightMedium>{props.count}</WeightMedium>
                                </TextColor>
                            </FontSize>
                        </FontRobotoSlab>
                    </TextCenter>
                    <TextCenter>
                        <FontQuicksand>
                            <FontSize size={FrontFontSizes.X_SMALL} lineHeight="1.25">
                                <TextColor color={props.isSelected ? FrontColors.NEUTRAL_18 : FrontColors.NEUTRAL_INK_LIGHT}>
                                    <Uppercase>
                                        <WeightMedium>{props.label}</WeightMedium>
                                    </Uppercase>
                                </TextColor>
                            </FontSize>
                        </FontQuicksand>
                    </TextCenter>
                </SpacingColumn>
            </Padding>
        </Background>
    </CursorPointer>
);

const NeedsAttention = (props: {
    cases: ReadonlyArray<TCase>;
    asIntroducerId: string | undefined;
}): JSX.Element => {
    const attentionCases = props.cases.filter((c) => c.outstanding_introducer_docs > 0);

    return (
        <SpacingColumn spacing={FrontSpacing.SMALL_3}>
            <SectionLabel>Needs attention</SectionLabel>
            {attentionCases.length === 0 ? (
                <Background color={FrontColors.NEUTRAL_18} borderRadius="5px">
                    <Padding type="custom" spacing={`${FrontSpacing.MEDIUM_1} ${FrontSpacing.MEDIUM_1}`}>
                        <FontQuicksand>
                            <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                                <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                                    <WeightMedium>You&apos;re all caught up — no documents outstanding.</WeightMedium>
                                </TextColor>
                            </FontSize>
                        </FontQuicksand>
                    </Padding>
                </Background>
            ) : (
                <Background color={FrontColors.PERTINENT_CORAL_21} borderRadius="5px">
                    <Padding type="custom" spacing={`${FrontSpacing.MEDIUM_1} ${FrontSpacing.MEDIUM_1}`}>
                        <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                            <FontQuicksand>
                                <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                                    <TextColor color={FrontColors.NEUTRAL_INK}>
                                        {attentionCases.length} {attentionCases.length === 1 ? "case is" : "cases are"} <WeightBold>waiting on documents from you</WeightBold>.
                                    </TextColor>
                                </FontSize>
                            </FontQuicksand>
                            <SpacingColumn spacing={FrontSpacing.SMALL_1}>
                                {attentionCases.map((c) => (
                                    <CursorPointer key={c.id} onClick={() => goToCase(c.id, props.asIntroducerId)}>
                                        <Background color={FrontColors.NEUTRAL_18} borderRadius="5px">
                                            <Padding type="custom" spacing={`${FrontSpacing.SMALL_2} ${FrontSpacing.SMALL_3}`}>
                                                <SpacingRow spacing={FrontSpacing.SMALL_2} justifyContent="space-between" alignItems="center">
                                                    <FontQuicksand>
                                                        <FontSize size={FrontFontSizes.X_SMALL} lineHeight="1.3">
                                                            <TextColor color={FrontColors.NEUTRAL_INK}>
                                                                <WeightBold>{formatAddress(c.address)}</WeightBold>
                                                            </TextColor>
                                                        </FontSize>
                                                    </FontQuicksand>
                                                    <span className="ip-dashboard__docs-pill">
                                                        {c.outstanding_introducer_docs} document{c.outstanding_introducer_docs === 1 ? "" : "s"} to upload →
                                                    </span>
                                                </SpacingRow>
                                            </Padding>
                                        </Background>
                                    </CursorPointer>
                                ))}
                            </SpacingColumn>
                        </SpacingColumn>
                    </Padding>
                </Background>
            )}
        </SpacingColumn>
    );
};

type TSectionColumns = {
    daysOnMarket: boolean;
    offers: boolean;
    viewings: boolean;
    legal: boolean;
};

const CasesSection = (props: {
    title: string;
    cases: ReadonlyArray<TCase>;
    bucketCounts: ReadonlyArray<{ bucket: string; label: string; count: number }>;
    bucketOrder: ReadonlyArray<string>;
    columns: TSectionColumns;
    asIntroducerId: string | undefined;
}): JSX.Element => {
    const [filterBucket, setFilterBucket] = useState<string | null>(null);
    const [sort, setSort] = useState<TSort>({ key: "stage", direction: "asc" });

    const visibleCases = useMemo(() => {
        const filtered = filterBucket === null
            ? props.cases
            : props.cases.filter((c) => c.bucket === filterBucket);
        return sortCases(filtered, sort, props.bucketOrder);
    }, [props.cases, props.bucketOrder, filterBucket, sort]);

    const toggleSort = (key: TSortKey): void => setSort((prev) => nextSortAfterClick(prev, key));

    const colSpan = 2 + (props.columns.daysOnMarket ? 1 : 0) + (props.columns.offers ? 1 : 0) + (props.columns.viewings ? 1 : 0);

    return (
        <SpacingColumn spacing={FrontSpacing.SMALL_3}>
            <SpacingRow spacing={FrontSpacing.SMALL_2} alignItems="center">
                <SectionLabel>{props.title}</SectionLabel>
                {filterBucket !== null && (
                    <CursorPointer onClick={() => setFilterBucket(null)}>
                        <FontQuicksand>
                            <FontSize size={FrontFontSizes.X_SMALL} lineHeight="1.2">
                                <TextColor color={FrontColors.NEUTRAL_18}>
                                    <WeightMedium>Clear filter</WeightMedium>
                                </TextColor>
                            </FontSize>
                        </FontQuicksand>
                    </CursorPointer>
                )}
            </SpacingRow>

            <SpacingRow spacing={FrontSpacing.SMALL_2} childSize="even-fill">
                {props.bucketCounts.map((b) => (
                    <BucketTile
                        key={b.bucket}
                        label={b.label}
                        count={b.count}
                        isSelected={filterBucket === b.bucket}
                        onClick={() => setFilterBucket(filterBucket === b.bucket ? null : b.bucket)}
                    />
                ))}
            </SpacingRow>

            <Background color={FrontColors.NEUTRAL_18} borderRadius="5px">
                <table className="ip-dashboard__table">
                    <thead>
                        <tr>
                            <SortableHeader label="Address" sortKey="address" sort={sort} onClick={toggleSort} />
                            <SortableHeader label="Stage" sortKey="stage" sort={sort} onClick={toggleSort} />
                            {props.columns.daysOnMarket && <SortableHeader label="Days on market" sortKey="days_on_market" sort={sort} onClick={toggleSort} />}
                            {props.columns.offers && <SortableHeader label="Offers (30d)" sortKey="offers" sort={sort} onClick={toggleSort} />}
                            {props.columns.viewings && <SortableHeader label="Viewings (30d)" sortKey="viewings" sort={sort} onClick={toggleSort} />}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleCases.length === 0 ? (
                            <tr>
                                <td className="ip-dashboard__td" colSpan={colSpan}>
                                    {filterBucket === null ? "No cases in this section yet." : "No cases at this stage."}
                                </td>
                            </tr>
                        ) : (
                            visibleCases.map((c) => {
                                const rowClassName = `ip-dashboard__row${c.outstanding_introducer_docs > 0 ? " ip-dashboard__row--attention" : ""}`;
                                return (
                                    <tr key={c.id} className={rowClassName} onClick={() => goToCase(c.id, props.asIntroducerId)}>
                                        <td className="ip-dashboard__td"><strong>{formatAddress(c.address)}</strong></td>
                                        <td className="ip-dashboard__td">
                                            <SpacingColumn spacing={FrontSpacing.SMALL_1}>
                                                <span>{BUCKET_LABELS[c.bucket]}</span>
                                                {props.columns.legal && c.legal_status !== null && (
                                                    <span className="ip-dashboard__legal-chip">{LEGAL_STATUS_LABELS[c.legal_status]}</span>
                                                )}
                                            </SpacingColumn>
                                        </td>
                                        {props.columns.daysOnMarket && <td className="ip-dashboard__td">{c.days_on_market === null ? "—" : c.days_on_market}</td>}
                                        {props.columns.offers && <td className="ip-dashboard__td">{c.offers_count_last_30d}</td>}
                                        {props.columns.viewings && <td className="ip-dashboard__td">{c.viewings_count_last_30d}</td>}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </Background>
        </SpacingColumn>
    );
};

export const IntroducerPortalDashboardPage = (): JSX.Element => {
    const [self, setSelf] = useState<TSelfData>(null);
    const [dashboard, setDashboard] = useState<TDashboardData>(null);
    const [cases, setCases] = useState<TCase[]>([]);
    const [loadStatus, setLoadStatus] = useState<"loading" | "ready" | "unauthorised" | "error">("loading");

    const asIntroducerId = useMemo(getAsIntroducerIdFromUrl, []);

    useEffect(() => {
        let cancelled = false;
        setLoadStatus("loading");

        Promise.all([
            getJson({
                url: withAsIntroducerId("/v1/introducer/me", asIntroducerId),
                dataCodec: SelfDataCodec,
                defaultData: null,
            })(),
            getJson({
                url: withAsIntroducerId("/v1/introducer/dashboard", asIntroducerId),
                dataCodec: DashboardDataCodec,
                defaultData: null,
            })(),
            getJson({
                url: withAsIntroducerId("/v1/introducer/cases?limit=100", asIntroducerId),
                dataCodec: CasesDataCodec,
                defaultData: [],
            })(),
        ]).then(([selfResp, dashResp, casesResp]) => {
            if (cancelled) return;

            const anyUnauthorised = [selfResp, dashResp, casesResp].some(
                (r) => r.tag !== FirstPartyFetchResponse.constants.STATUS_2XX,
            );
            if (anyUnauthorised) {
                setLoadStatus("unauthorised");
                return;
            }
            if (selfResp.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                setSelf(selfResp.response.data);
            }
            if (dashResp.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                setDashboard(dashResp.response.data);
            }
            if (casesResp.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                setCases(casesResp.response.data);
            }
            setLoadStatus("ready");
        });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (loadStatus === "unauthorised") {
            redirectToLogin();
        }
    }, [loadStatus]);

    const salesCases = useMemo(() => cases.filter((c) => c.section === "sales"), [cases]);
    const conveyancingCases = useMemo(() => cases.filter((c) => c.section === "conveyancing"), [cases]);

    if (loadStatus === "loading" || loadStatus === "unauthorised") {
        return (
            <PurpleShell>
                <StateHero
                    title={loadStatus === "unauthorised" ? "Taking you to sign-in." : "Loading your portfolio."}
                    body={loadStatus === "unauthorised"
                        ? <>One moment — we couldn&apos;t verify your session.</>
                        : <>Pulling together every case we&apos;re running for you. Just a moment.</>}
                />
            </PurpleShell>
        );
    }

    if (loadStatus === "error" || !self || !dashboard) {
        return (
            <PurpleShell>
                <StateHero
                    title="Something's off."
                    body={<>We couldn&apos;t load your portfolio. Refresh the page — if it keeps happening, contact your <WeightBold>Sail Legal</WeightBold> contact.</>}
                />
            </PurpleShell>
        );
    }

    const intro = self.introducer;
    const salesTotal = SALES_BUCKET_ORDER.reduce((sum, b) => sum + dashboard.sales_buckets[b], 0);
    const conveyancingTotal = CONVEYANCING_BUCKET_ORDER.reduce((sum, b) => sum + dashboard.conveyancing_buckets[b], 0);

    const salesBucketCounts = SALES_BUCKET_ORDER.map((b) => ({
        bucket: b,
        label: SALES_BUCKET_LABELS[b],
        count: dashboard.sales_buckets[b],
    }));
    const conveyancingBucketCounts = CONVEYANCING_BUCKET_ORDER.map((b) => ({
        bucket: b,
        label: CONVEYANCING_BUCKET_LABELS[b],
        count: dashboard.conveyancing_buckets[b],
    }));

    return (
        <PurpleShell>
            <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                {self.is_impersonating && <ImpersonationBanner introducerName={intro.name} />}
                {/* Header */}
                <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                    <TextCenter>
                        <FPSimpleLogo width="48px" />
                    </TextCenter>
                    <TextCenter>
                        <FontRobotoSlab>
                            <FontSize size={FrontFontSizes.BIG} lineHeight="1.1">
                                <TextColor color={FrontColors.NEUTRAL_18}>
                                    <WeightMedium>{intro.name}.</WeightMedium>
                                </TextColor>
                            </FontSize>
                        </FontRobotoSlab>
                    </TextCenter>
                    <TextCenter>
                        <FontQuicksand>
                            <FontSize size={FrontFontSizes.SMALL} lineHeight="1.4">
                                <TextColor color={FrontColors.NEUTRAL_18}>
                                    {self.is_introducer_admin
                                        ? <>Portfolio overview — <WeightBold>{dashboard.total_cases}</WeightBold> case{dashboard.total_cases === 1 ? "" : "s"} for {intro.name}.</>
                                        : <>Your cases — <WeightBold>{dashboard.total_cases}</WeightBold> case{dashboard.total_cases === 1 ? "" : "s"}.</>}
                                </TextColor>
                            </FontSize>
                        </FontQuicksand>
                    </TextCenter>
                </SpacingColumn>

                {/* 1. What must I do? */}
                <NeedsAttention cases={cases} asIntroducerId={asIntroducerId} />

                {/* 2. How are my sales going? — only when Sail Homes is marketing cases */}
                {salesTotal > 0 && (
                    <CasesSection
                        title="Sales"
                        cases={salesCases}
                        bucketCounts={salesBucketCounts}
                        bucketOrder={SALES_BUCKET_ORDER}
                        columns={{ daysOnMarket: true, offers: true, viewings: true, legal: true }}
                        asIntroducerId={asIntroducerId}
                    />
                )}

                {/* 3. How are my legals going? */}
                {conveyancingTotal > 0 && (
                    <CasesSection
                        title="Conveyancing"
                        cases={conveyancingCases}
                        bucketCounts={conveyancingBucketCounts}
                        bucketOrder={CONVEYANCING_BUCKET_ORDER}
                        columns={{ daysOnMarket: false, offers: false, viewings: false, legal: false }}
                        asIntroducerId={asIntroducerId}
                    />
                )}

                {salesTotal === 0 && conveyancingTotal === 0 && (
                    <Background color={FrontColors.NEUTRAL_18} borderRadius="5px">
                        <Padding type="custom" spacing={`${FrontSpacing.LARGE_1} ${FrontSpacing.MEDIUM_1}`}>
                            <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                                <TextCenter>
                                    <FontRobotoSlab>
                                        <FontSize size={FrontFontSizes.LARGE} lineHeight="1.2">
                                            <TextColor color={FrontColors.NEUTRAL_INK}>
                                                <WeightMedium>No cases yet.</WeightMedium>
                                            </TextColor>
                                        </FontSize>
                                    </FontRobotoSlab>
                                </TextCenter>
                                <Flex justifyContent="center">
                                    <MaxWidth width="460px">
                                        <FontQuicksand>
                                            <FontSize size={FrontFontSizes.SMALL} lineHeight={1.45}>
                                                <TextColor color={FrontColors.NEUTRAL_INK_LIGHT}>
                                                    <TextAlign position="center">
                                                        Your portfolio will populate as Sail Legal instructs cases on your behalf.
                                                    </TextAlign>
                                                </TextColor>
                                            </FontSize>
                                        </FontQuicksand>
                                    </MaxWidth>
                                </Flex>
                            </SpacingColumn>
                        </Padding>
                    </Background>
                )}
            </SpacingColumn>
        </PurpleShell>
    );
};

export default IntroducerPortalDashboardPage;
